import React, { useEffect, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { Platform, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { capitalize } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import {
  CODE_STATUS,
  codeFieldLabel,
  EmailClient,
  enterAValidCodeErrorMessage,
  genericErrorMessage,
  getEmailAddressClientFromString,
  RESEND_EMAIL_TIMEOUT_DURATION_SECONDS,
  thisFieldIsRequiredErrorMessage,
  useCountdownTimer,
} from '@lyrahealth-inc/shared-app-logic'
import {
  AccessibilityRolesNative,
  FormContainer,
  getAuroraAwareBackgroundColor,
  InlineErrorBanner,
  LoadingIndicator,
  ThemeType,
  tID,
  Toast,
  ToastContentWithAnimationEndFunction,
} from '@lyrahealth-inc/ui-core-crossplatform'

import { IS_WEB } from '../..'
import { PressableOpacity, PrimaryButton } from '../../atoms'
import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { ArrowIcon, ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { CheckEmailAnimation } from '../../atoms/illustrations/CheckEmailAnimation'
import { Link } from '../../atoms/link/Link'
import { Subhead, Size as SubheadSize } from '../../atoms/subhead/Subhead'
import { CodeInputRFF } from '../../molecules/codeInput/CodeInput'

export type OneTimePasscodeProps = {
  codeStatus?: CODE_STATUS
  contentContainerStyle?: CSSObject
  email: string
  initialValues?: { code: string }
  isLoading?: boolean
  isPreview?: boolean
  isSubmitting?: boolean
  length: number
  toastProps?: ToastContentWithAnimationEndFunction
  showErrorMessage?: boolean
  showBackButton?: boolean
  onContactUsPressed: () => void
  onOpenEmailPressed: (email?: string) => void
  onResendCode: () => void
  onSubmit: (values: { code: string }) => void
  onBackPressed?: () => void
}

export const SUBMIT_BUTTON_ID = 'OneTimePasscode-submit'

const LoadingIndicatorContainer = styled(LoadingIndicator)(({}) => ({
  marginTop: 50,
  textAlign: 'center',
}))

const SubmitButtonContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'center',
})

const SubmitButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  width: theme.breakpoints.isMinWidthLaptop ? '343px' : '100%',
  marginTop: theme.spacing['16px'],
}))

const BackgroundContainer = styled(SafeAreaView)<{ theme: ThemeType; isPreview: boolean }>(({ theme, isPreview }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(theme.isAuroraTheme, () =>
    theme.breakpoints.isMinWidthTablet ? 'none' : theme.colors.backgroundPrimary,
  ),
  height: '100%',
  marginBottom: isPreview ? 0 : theme.breakpoints.isMobileSized ? theme.spacing['24px'] : theme.spacing['32px'],
}))

const ContentContainer = styled.View<{ theme: ThemeType; contentContainerStyle?: CSSObject; isPreview?: boolean }>(
  ({ theme, contentContainerStyle, isPreview }) => ({
    backgroundColor: getAuroraAwareBackgroundColor(theme.isAuroraTheme, theme.colors.backgroundPrimary),
    paddingHorizontal: theme.spacing['16px'],
    justifyContent: 'center',
    alignSelf: 'center',
    ...(theme.breakpoints.isMinWidthTablet && {
      width: '624px',
      borderRadius: '8px',
      padding: '48px',
      marginTop: '72px',
      marginBottom: isPreview ? '0px' : '300px',
    }),
    ...(theme.breakpoints.isMobileSized &&
      isPreview && {
        marginBottom: '128px',
      }),
    ...contentContainerStyle,
  }),
)

const ImageContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['32px'],
  alignItems: 'center',
}))

const BodyTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

const ErrorMessageContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const ArrowContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
  marginBottom: theme.spacing['16px'],
  paddingHorizontal: theme.spacing['16px'],
}))

const CountdownContainer = styled(View)({
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
})

const CountdownText = styled(BodyText)(({ theme }) => ({
  color: theme.colors.textInactive,
  lineHeight: IS_WEB ? 24 : 20,
  marginBottom: Platform.OS === 'android' ? -4 : 0, // Vertically align text for Android
}))

const DisabledText = styled(BodyText)(({ theme }) => ({
  color: theme.colors.textInactive,
}))

export const OneTimePasscode: React.FunctionComponent<OneTimePasscodeProps> = ({
  codeStatus,
  contentContainerStyle,
  email,
  initialValues,
  isLoading = false,
  isPreview = false,
  isSubmitting = false,
  length,
  toastProps,
  showErrorMessage,
  showBackButton = false,
  onContactUsPressed,
  onOpenEmailPressed,
  onResendCode,
  onSubmit,
  onBackPressed,
}) => {
  const { formatMessage } = useIntl()
  const { breakpoints, colors } = useTheme() as ThemeType

  const handleAutoSubmit = () => {
    setTimeout(() => {
      if (IS_WEB) {
        // Focus submit button on auto-submit
        const submitButton = document.getElementById(SUBMIT_BUTTON_ID) as HTMLElement
        if (submitButton) {
          submitButton.focus()
        }
      }
    }, 100)
  }

  const [duration, setDuration] = useState(0)
  const [countdown] = useCountdownTimer(duration, IS_WEB)

  useEffect(() => {
    if (countdown === 0) {
      setDuration(0)
    }
  }, [countdown, setDuration])

  const onResendCodePress = () => {
    setDuration(RESEND_EMAIL_TIMEOUT_DURATION_SECONDS)
    onResendCode()
  }

  const isResendDisabled = countdown !== 0

  const formValidation = (values: { code: string }) => {
    const errors: { code?: string } = {}
    if (!values.code) {
      errors.code = formatMessage(thisFieldIsRequiredErrorMessage)
    } else if (values.code && values.code.length !== length) {
      errors.code = formatMessage(enterAValidCodeErrorMessage)
    }
    return errors
  }

  const submitCodeText = formatMessage({
    defaultMessage: 'Submit',
    description: 'Button text for submitting a one time passcode',
  })

  const title = formatMessage({
    defaultMessage: 'Enter the code sent to your email',
    description: 'Header text prompting the user to enter their one time passcode',
  })

  const emailClient = getEmailAddressClientFromString(email) as EmailClient
  let emailClientLink
  if (emailClient && Object.values(EmailClient).includes(emailClient)) {
    if (!IS_WEB) {
      emailClientLink = (
        <Link
          text={formatMessage({
            defaultMessage: 'Open email',
            description: 'Link text to open email application',
          })}
          onPress={() => onOpenEmailPressed()}
          testID={tID('OneTimePasscode-openEmail-link')}
        />
      )
    } else {
      emailClientLink = (
        <Link
          text={formatMessage(
            {
              defaultMessage: 'Open {emailClient}',
              description: 'Link text to open email application',
            },
            { emailClient: capitalize(emailClient) },
          )}
          onPress={() => onOpenEmailPressed(email)}
          testID={tID('OneTimePasscode-openEmail-link')}
        />
      )
    }
  }

  const bodyText = (
    <>
      <FormattedMessage
        defaultMessage='We sent a six-character code to {email}.'
        description='Subheader text informing the user an email was sent to them containing their one time passcode'
        values={{
          email: email,
        }}
      />
      {emailClientLink && <>&nbsp;{emailClientLink}</>}
    </>
  )

  if (isLoading) {
    return <LoadingIndicatorContainer size={45} />
  }

  return (
    <>
      <BackgroundContainer isPreview={isPreview}>
        {showBackButton && onBackPressed && breakpoints.isMobileSized && (
          <ArrowContainer onPress={onBackPressed} testID={tID('backButton')}>
            <ArrowIcon fillColor={colors.iconDefault} direction={ArrowIconDirection.LEFT} />
          </ArrowContainer>
        )}
        <ContentContainer
          testID={tID('OneTimePasscode')}
          contentContainerStyle={contentContainerStyle}
          isPreview={isPreview}
        >
          <ImageContainer>
            <CheckEmailAnimation />
          </ImageContainer>
          <Subhead text={title} size={SubheadSize.LARGE} />

          <>
            <BodyTextContainer>
              <BodyText text={bodyText} size={Size.DEFAULT} color={colors.textSecondary} />
            </BodyTextContainer>
            <Form
              onSubmit={onSubmit}
              validate={formValidation}
              initialValues={initialValues}
              render={({ errors, handleSubmit, touched }) => (
                <FormContainer handleSubmit={handleSubmit}>
                  {showErrorMessage && (
                    <ErrorMessageContainer>
                      <InlineErrorBanner text={formatMessage(genericErrorMessage)} />
                    </ErrorMessageContainer>
                  )}
                  <Field
                    component={CodeInputRFF}
                    codeStatus={codeStatus}
                    error={touched && errors?.code}
                    handleSubmit={handleSubmit}
                    label={formatMessage(codeFieldLabel)}
                    length={6}
                    name='code'
                    returnKeyType='done'
                    shouldAutoSubmit={true}
                    onAutoSubmit={handleAutoSubmit}
                  />
                  <BodyText
                    text={
                      <FormattedMessage
                        defaultMessage={`The code expires in 12 hours. If you can’t find the email, check your spam folder. Need help? <resendCodeLink>Resend the code</resendCodeLink> or <phoneNumbersLink>contact us</phoneNumbersLink>.`}
                        description='Text informing the user to check if email is sent to their spam folder and to contact Lyra Health directly if they do not receive an email'
                        values={{
                          resendCodeLink: (chunks: string) => {
                            if (isResendDisabled) {
                              const countdownText = Math.floor(countdown).toString().padStart(2, '0')
                              return (
                                <>
                                  <DisabledText text={chunks} size={Size.DEFAULT} color={colors.textInactive} />
                                  <DisabledText
                                    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                                    text={' ('}
                                    size={Size.DEFAULT}
                                  />
                                  <CountdownContainer testID={tID('OneTimePasscode-resendCode-link-countdown')}>
                                    <CountdownText
                                      text={countdownText}
                                      size={Size.DEFAULT}
                                      aria-live='polite'
                                      accessibilityRole='text'
                                      accessibilityLabel={formatMessage(
                                        {
                                          defaultMessage:
                                            '{countdown} seconds remaining before you can resend the code',
                                          description:
                                            'Accessibility label for countdown timer before user can resend code',
                                        },
                                        { countdown: Math.floor(countdown) },
                                      )}
                                      accessibilityLiveRegion='polite'
                                    />
                                  </CountdownContainer>
                                  <DisabledText
                                    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                                    text={')'}
                                    size={Size.DEFAULT}
                                  />
                                </>
                              )
                            } else {
                              return (
                                <Link
                                  onPress={onResendCodePress}
                                  size={Size.DEFAULT}
                                  testID={tID('OneTimePasscode-resendCode-link')}
                                  text={chunks}
                                  underline
                                />
                              )
                            }
                          },
                          phoneNumbersLink: (chunks: string) => (
                            <Link
                              onPress={onContactUsPressed}
                              size={Size.DEFAULT}
                              testID={tID('OneTimePasscode-contactUs-link')}
                              text={chunks}
                              underline
                            />
                          ),
                        }}
                      />
                    }
                    size={Size.DEFAULT}
                    color={colors.textSecondary}
                  />
                  <SubmitButtonContainer>
                    <SubmitButton
                      accessibilityLabel={submitCodeText}
                      accessibilityRole={AccessibilityRolesNative.BUTTON}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      onPress={handleSubmit}
                      text={submitCodeText}
                      id={SUBMIT_BUTTON_ID}
                      testID={tID(SUBMIT_BUTTON_ID)}
                    />
                  </SubmitButtonContainer>
                </FormContainer>
              )}
            />
          </>
        </ContentContainer>
      </BackgroundContainer>
      {toastProps && <Toast {...toastProps} containerStyle={{ bottom: 24 }} />}
    </>
  )
}
